





















































































































import {
    defineComponent,
    ref,
    computed,
    onMounted,
} from "@vue/composition-api";
import NmeaViewModel from "@/models/NmeaViewModel";
import useNMEAData from "@/store/useNMEAData";
export default defineComponent({
    components: {
        SensorHeader: () =>
            import(
                /* webpackChunkName: "components" */ "./Sensors/SensorHeader.vue"
            ),
    },
    setup() {
        const panel = ref();
        const loading = ref(false);
        const error = ref<string | null>(null);
        const nmea = ref<NmeaViewModel | null>(null);
        const { getNMEAData } = useNMEAData();

        const toNumber = (value: unknown): number | null => {
            if (value === null || value === undefined) return null;
            const num = typeof value === "number" ? value : Number(value);
            return Number.isFinite(num) ? num : null;
        };

        const formatValue = (value: number, digits: number, unit?: string) => {
            const text = value.toFixed(digits);
            return unit ? `${text} ${unit}` : text;
        };

        const lastUpdated = computed(() => {
            if (!nmea.value?.sampledAt) return null;
            const date = new Date(nmea.value.sampledAt as any);
            if (Number.isNaN(date.getTime())) return null;
            return date.toLocaleString();
        });

        const sections = computed(() => {
            if (!nmea.value) return [] as Array<any>;
            const data = nmea.value;
            const result: Array<any> = [];

            const pushSection = (section: any) => {
                if (section.items.length > 0) result.push(section);
            };

            // Navigation
            const navItems: Array<any> = [];
            const lat = toNumber(data.lat);
            const lon = toNumber(data.lon);
            if (lat !== null && lon !== null) {
                navItems.push({
                    label: "Position",
                    value: `${lat.toFixed(6)}, ${lon.toFixed(6)}`,
                });
            }
            const sog = toNumber(data.sog);
            if (sog !== null) {
                navItems.push({
                    label: "Speed (SOG)",
                    value: formatValue(sog, 2, "m/s"),
                });
            }
            const cog = toNumber(data.cog);
            if (cog !== null) {
                navItems.push({
                    label: "Course (COG)",
                    value: formatValue(cog, 1, "°"),
                });
            }
            pushSection({
                title: "Navigation",
                icon: "mdi-navigation",
                items: navItems,
            });

            // Heading / Motion
            const hdgItems: Array<any> = [];
            const hdgMag = toNumber(data.hdgMag);
            if (hdgMag !== null) {
                hdgItems.push({
                    label: "Heading (Mag)",
                    value: formatValue(hdgMag, 1, "°"),
                });
            }
            const hdgTrue = toNumber(data.hdgTrue);
            if (hdgTrue !== null) {
                hdgItems.push({
                    label: "Heading (True)",
                    value: formatValue(hdgTrue, 1, "°"),
                });
            }
            const rudder = toNumber(data.rudder);
            if (rudder !== null) {
                hdgItems.push({
                    label: "Rudder Angle",
                    value: formatValue(rudder, 1, "°"),
                });
            }
            pushSection({
                title: "Heading & Motion",
                icon: "mdi-compass",
                items: hdgItems,
            });

            // Engine
            const engItems: Array<any> = [];
            const rpm = toNumber(data.rpm);
            if (rpm !== null) {
                engItems.push({
                    label: "RPM",
                    value: formatValue(rpm, 0),
                });
            }
            const engHours = toNumber(data.engHours);
            if (engHours !== null) {
                engItems.push({
                    label: "Engine Hours",
                    value: formatValue(engHours, 1, "h"),
                });
            }
            const engTemp = toNumber(data.engTemp);
            if (engTemp !== null) {
                engItems.push({
                    label: "Engine Temp",
                    value: formatValue(engTemp, 1, "°C"),
                });
            }
            const engTempCoolant = toNumber(data.engTempCoolant);
            if (engTempCoolant !== null) {
                engItems.push({
                    label: "Coolant Temp",
                    value: formatValue(engTempCoolant, 1, "°C"),
                });
            }
            const engTempOil = toNumber(data.engTempOil);
            if (engTempOil !== null) {
                engItems.push({
                    label: "Oil Temp",
                    value: formatValue(engTempOil, 1, "°C"),
                });
            }
            pushSection({
                title: "Engine",
                icon: "mdi-engine",
                items: engItems,
            });

            // Fuel
            const fuelItems: Array<any> = [];
            const fuelLevel = toNumber(data.fuelLevel);
            if (fuelLevel !== null) {
                fuelItems.push({
                    label: "Fuel Level",
                    value: formatValue(fuelLevel, 1, "%"),
                });
            }
            const fuelRate = toNumber(data.fuelRate);
            if (fuelRate !== null) {
                fuelItems.push({
                    label: "Fuel Rate",
                    value: formatValue(fuelRate, 2, "L/h"),
                });
            }
            pushSection({ title: "Fuel", icon: "mdi-fuel", items: fuelItems });

            // Environment
            const envItems: Array<any> = [];
            const waterTemp = toNumber(data.waterTemp);
            if (waterTemp !== null) {
                envItems.push({
                    label: "Water Temp",
                    value: formatValue(waterTemp, 1, "°C"),
                });
            }
            const airTemp = toNumber(data.airTemp);
            if (airTemp !== null) {
                envItems.push({
                    label: "Air Temp",
                    value: formatValue(airTemp, 1, "°C"),
                });
            }
            const depth = toNumber(data.depth);
            if (depth !== null) {
                envItems.push({
                    label: "Depth",
                    value: formatValue(depth, 2, "m"),
                });
            }
            pushSection({
                title: "Environment",
                icon: "mdi-thermometer",
                items: envItems,
            });

            // Wind
            const windItems: Array<any> = [];
            const windSpeed = toNumber(data.windSpeed);
            if (windSpeed !== null) {
                windItems.push({
                    label: "Wind Speed",
                    value: formatValue(windSpeed, 2, "m/s"),
                });
            }
            const windAngle = toNumber(data.windAngle);
            if (windAngle !== null) {
                windItems.push({
                    label: "Wind Angle",
                    value: formatValue(windAngle, 1, "°"),
                });
            }
            const windBadge =
                data.windTrue === null || data.windTrue === undefined
                    ? null
                    : data.windTrue
                    ? "TRUE"
                    : "APPARENT";
            pushSection({
                title: "Wind",
                icon: "mdi-weather-windy",
                badge: windBadge,
                badgeColor: windBadge === "TRUE" ? "green" : "blue",
                items: windItems,
            });

            // Electrical
            const elecItems: Array<any> = [];
            const voltage = toNumber(data.voltage);
            if (voltage !== null) {
                elecItems.push({
                    label: "Voltage",
                    value: formatValue(voltage, 2, "V"),
                });
            }
            const battCurrent = toNumber(data.battCurrent);
            if (battCurrent !== null) {
                elecItems.push({
                    label: "Battery Current",
                    value: formatValue(battCurrent, 2, "A"),
                });
            }
            pushSection({
                title: "Electrical",
                icon: "mdi-flash",
                items: elecItems,
            });

            // Tanks
            const tankItems: Array<any> = [];
            const freshWater = toNumber(data.freshWater);
            if (freshWater !== null) {
                tankItems.push({
                    label: "Fresh Water",
                    value: formatValue(freshWater, 1, "%"),
                });
            }
            pushSection({
                title: "Tanks",
                icon: "mdi-water",
                items: tankItems,
            });

            return result;
        });

        const refresh = async () => {
            loading.value = true;
            error.value = null;
            try {
                nmea.value = await getNMEAData();
            } catch (err) {
                error.value = "Unable to load NMEA data.";
            } finally {
                loading.value = false;
            }
        };

        onMounted(refresh);

        return {
            panel,
            loading,
            error,
            sections,
            lastUpdated,
            refresh,
        };
    },
});
